import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserX } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const NICHTANTRITT_GRUENDE = {
  krankheit: "Krankheit",
  arbeitsaufnahme: "Arbeitsaufnahme",
  kein_interesse: "Kein Interesse mehr",
  finanzierung: "Finanzierung nicht zustande gekommen",
  nicht_erreichbar: "Nicht erschienen / nicht erreichbar",
  persoenlich: "Persönliche / familiäre Gründe",
  sonstige: "Sonstiger Grund",
};
const deDatum = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "");

const NichtantrittDialog = ({ open, onClose, teilnehmer, onSaved }) => {
  const [f, setF] = useState({ datum: teilnehmer?.kurs_start?.slice(0, 10) || new Date().toISOString().slice(0, 10), grund: "", kommentar: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/teilnehmer/${teilnehmer.id}/nichtantritt`, f);
      toast.success(`Nichtantritt gespeichert${r.data.rechnungen_markiert ? ` – ${r.data.rechnungen_markiert} offene Rechnung(en) markiert` : ""}`);
      onClose();
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };
  const feld = "w-full border border-stone-300 rounded px-2 py-1.5 text-sm";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="nichtantritt-dialog">
        <DialogHeader><DialogTitle>Nichtantritt erfassen</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="block">Datum (geplanter Kursbeginn bzw. Tag der Absage)
            <input type="date" value={f.datum} onChange={(e) => setF({ ...f, datum: e.target.value })} className={feld} data-testid="nichtantritt-datum" />
          </label>
          <div>
            <span className="block mb-1">Grund</span>
            <Select value={f.grund} onValueChange={(v) => setF({ ...f, grund: v })}>
              <SelectTrigger data-testid="nichtantritt-grund"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
              <SelectContent>
                {Object.entries(NICHTANTRITT_GRUENDE).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`nichtantritt-grund-${k}`}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="block">Bemerkung {f.grund === "sonstige" ? "(Pflicht)" : "(optional)"}
            <textarea rows={2} value={f.kommentar} onChange={(e) => setF({ ...f, kommentar: e.target.value })} className={feld} data-testid="nichtantritt-kommentar" />
          </label>
          <p className="text-xs text-stone-500">Offene Rechnungen werden nur markiert, nicht storniert. Über Storno entscheiden Sie selbst.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={busy || !f.grund || !f.datum} data-testid="nichtantritt-speichern" className="bg-red-700 hover:bg-red-800 text-white">
            {busy ? "Speichert…" : "Nichtantritt speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const NichtantrittKarte = ({ teilnehmer, onChanged }) => {
  const [open, setOpen] = useState(false);
  if (!teilnehmer || teilnehmer.status === "abgebrochen") return null;
  const zurueck = async () => {
    if (!window.confirm("Nichtantritt zurücknehmen? Status wird wieder „aktiv“, die Markierung an offenen Rechnungen wird entfernt.")) return;
    try {
      await axios.post(`${API}/teilnehmer/${teilnehmer.id}/nichtantritt-zuruecknehmen`);
      toast.success("Nichtantritt zurückgenommen");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const erfasst = teilnehmer.status === "nicht_angetreten";
  return (
    <Card data-testid="nichtantritt-card" className="border-red-200 bg-red-50/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-red-800"><UserX size={18} /> Nichtantritt</CardTitle>
      </CardHeader>
      <CardContent>
        {erfasst ? (
          <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="nichtantritt-erfasst">
            <span className="px-3 py-1.5 rounded-md border border-red-300 bg-red-50 text-red-800 font-medium">
              Nicht angetreten{teilnehmer.nichtantritt_datum ? ` am ${deDatum(teilnehmer.nichtantritt_datum)}` : ""}
              {teilnehmer.nichtantritt_grund ? ` · ${NICHTANTRITT_GRUENDE[teilnehmer.nichtantritt_grund] || "BG-Austrittsmeldung"}` : ""}
            </span>
            {teilnehmer.nichtantritt_kommentar && <span className="text-stone-600 italic">{teilnehmer.nichtantritt_kommentar}</span>}
            <Button variant="ghost" size="sm" onClick={zurueck} data-testid="nichtantritt-zuruecknehmen">Zurücknehmen</Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-stone-600">Für alle Finanzierungsarten: Datum und Grund werden gespeichert, offene Rechnungen markiert.</p>
            <Button variant="outline" className="border-red-500 text-red-700 hover:bg-red-100" onClick={() => setOpen(true)} data-testid="nichtantritt-erfassen">
              <UserX size={16} className="mr-2" /> Nichtantritt erfassen
            </Button>
          </div>
        )}
        {open && <NichtantrittDialog open={open} onClose={() => setOpen(false)} teilnehmer={teilnehmer} onSaved={onChanged} />}
      </CardContent>
    </Card>
  );
};
